import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, MessageSquare, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import {
  getWaThreads, getWaMessages, sendWaMessage, markWaRead
} from '../../api/client';

const fmtDT = (d: string) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

export default function WhatsAppInbox() {
  const toast = useToast();
  const [threads, setThreads] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [activePhone, setActivePhone] = useState('');
  const [activeName, setActiveName] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const chatEnd = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  const loadThreads = useCallback(async () => {
    try { setLoading(true); setThreads(((await getWaThreads()) as any)?.threads || []); } catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openChat = useCallback(async (phone: string, name?: string) => {
    setActivePhone(phone);
    setActiveName(name || phone);
    try {
      const d: any = await getWaMessages({ phone });
      setMessages(d?.messages || []);
      markWaRead(phone);
      setThreads(prev => prev.map(t => t.phone === phone ? { ...t, unread: 0 } : t));
    } catch (e: any) { toast.show(e.message, 'error'); }
    setTimeout(() => chatEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, []);

  useEffect(() => {
    if (!activePhone) return;
    pollRef.current = setInterval(async () => {
      try { setMessages(((await getWaMessages({ phone: activePhone })) as any)?.messages || []); } catch { /* */ }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activePhone]);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!msg.trim() || !activePhone) return;
    setSending(true);
    try {
      const r: any = await sendWaMessage(activePhone, msg);
      if (r.success) { setMsg(''); openChat(activePhone, activeName); }
      else toast.show(r.error || 'Failed to send', 'error');
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSending(false); }
  };

  const filtered = search ? threads.filter(t =>
    (t.name || '').toLowerCase().includes(search.toLowerCase()) || t.phone.includes(search)
  ) : threads;

  const totalUnread = threads.reduce((s: number, t: any) => s + (t.unread || 0), 0);

  return (
    <div className="animate-in fade-in">
      <div className="flex bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 240px)', minHeight: 500 }}>
        {/* Thread list */}
        <div className={`w-full md:w-80 flex-shrink-0 border-r flex flex-col ${activePhone ? 'hidden md:flex' : ''}`}>
          <div className="p-3 border-b">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={18} className="text-green-600" />
              <h3 className="font-bold text-sm">WhatsApp Inbox</h3>
              {totalUnread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">{totalUnread}</span>}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="w-full pl-8 pr-3 py-1.5 text-xs border rounded-lg" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={24} /></div> :
              filtered.length === 0 ? <p className="text-center text-slate-400 text-xs py-8">No conversations</p> :
              filtered.map(t => (
                <button key={t.phone} onClick={() => openChat(t.phone, t.name || t.phone)}
                  className={`w-full text-left px-3 py-2.5 border-b hover:bg-slate-50 transition ${activePhone === t.phone ? 'bg-green-50' : ''}`}>
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                        {t.name || t.phone}
                        <span className={`text-[8px] px-1 py-0.5 rounded font-bold ${t.userType === 'employer' ? 'bg-blue-100 text-blue-600' : t.userType === 'chef' ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>
                          {t.userType === 'employer' ? 'EMP' : t.userType === 'chef' ? 'CHEF' : t.userType === 'student' ? 'STU' : '?'}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {t.lastDirection === 'outgoing' && <span className="text-green-600">You: </span>}
                        {t.lastMessage || 'No messages'}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className="text-[9px] text-slate-400">{t.lastAt ? fmtDT(t.lastAt) : ''}</div>
                      {t.unread > 0 && <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-green-500 text-white font-bold">{t.unread}</span>}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>

        {/* Chat area */}
        <div className={`flex-1 flex flex-col ${!activePhone ? 'hidden md:flex' : ''}`}>
          {!activePhone ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-3 opacity-30" />
                <p>Select a conversation</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b flex items-center gap-3 bg-slate-50">
                <button className="md:hidden p-1" onClick={() => setActivePhone('')}><ArrowLeft size={18} /></button>
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><MessageSquare size={14} className="text-green-600" /></div>
                <div>
                  <div className="font-semibold text-sm">{activeName}</div>
                  <div className="text-[10px] text-slate-400">{activePhone}</div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#f0f2f5]">
                {messages.map((m: any) => (
                  <div key={m.id} className={`flex ${m.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${
                      m.direction === 'outgoing' ? 'bg-green-100 text-slate-800 rounded-br-sm' : 'bg-white text-slate-800 rounded-bl-sm shadow-sm'
                    }`}>
                      {m.media_url && <a href={m.media_url} target="_blank" rel="noopener" className="text-blue-600 text-xs underline block mb-1">[{m.msg_type}]</a>}
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        <span className="text-[9px] text-slate-400">{fmtDT(m.created_at)}</span>
                        {m.direction === 'outgoing' && (
                          <span className={`text-[9px] ${m.status === 'read' ? 'text-blue-500' : m.status === 'delivered' ? 'text-slate-400' : m.status === 'failed' ? 'text-red-500' : 'text-slate-300'}`}>
                            {m.status === 'read' ? '✓✓' : m.status === 'delivered' ? '✓✓' : m.status === 'failed' ? '✗' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={chatEnd} />
              </div>

              <div className="p-3 border-t bg-white flex gap-2">
                <input
                  className="flex-1 text-sm border rounded-full px-4 py-2 outline-none focus:ring-2 focus:ring-green-300"
                  placeholder="Type a message..."
                  value={msg} onChange={e => setMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
                />
                <button onClick={handleSend} disabled={sending || !msg.trim()}
                  className="w-10 h-10 rounded-full bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white flex items-center justify-center transition">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
